/**
 * Template Build Page
 *
 * Page component for AI-powered template creation.
 * Features:
 * - Chat sidebar on the right for AI interactions
 * - Work area on the left for displaying template preview and sections
 * - Supports document upload or text paste to start
 */

import React, { useCallback, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAdminBuildSaveMutation } from '@features/admin-v2/store/hooks';
import { EntitlementGate } from '@features/entitlements';
import { templatesApi } from '@features/templates/store/api';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useFeature } from '@store/features/hooks';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { TemplateBuilderChat } from '../components/TemplateBuilderChat';
import { TemplateWorkArea } from '../components/TemplateWorkArea';
import { useAdminTemplateBuildStatus } from '../hooks/useAdminTemplateBuildStatus';
import { useTemplateBuildActions } from '../hooks/useTemplateBuildActions';
import { useTemplateBuildStatus } from '../hooks/useTemplateBuildStatus';
import { useSaveTemplateBuildMutation } from '../store/api';
import {
  initializeTemplateBuild,
  resetBuild,
  setBuildStatus,
  setCreatedTemplateId,
  setTaskId,
} from '../store/chatSlice';
import { templatesAiEditFeature } from '../store/feature';
import {
  selectAssistantStatus,
  selectBuildStatus,
  selectDraftTemplate,
  selectMessages,
  selectTaskId,
} from '../store/selectors';

// Chat sidebar width
const CHAT_WIDTH = 420;

/**
 * Page component for the Template Build feature
 */
export const TemplateBuildPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{ taskId?: string }>();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { templateBuild } = useEventTracking();

  // Extract taskId from URL params
  const urlTaskId = params.taskId || null;

  // Check if in admin mode
  const isAdminMode = searchParams.get('admin') === 'true';
  const targetUserId = searchParams.get('targetUserId') || null;

  // Ensure feature is registered
  const isFeatureReady = useFeature(templatesAiEditFeature);

  // Redux state
  const messages = useAppSelector(selectMessages);
  const assistantStatus = useAppSelector(selectAssistantStatus);
  const storeTaskId = useAppSelector(selectTaskId);
  const buildStatus = useAppSelector(selectBuildStatus);
  const draftTemplate = useAppSelector(selectDraftTemplate);

  // Effective task ID - URL param takes precedence
  const effectiveTaskId = urlTaskId || storeTaskId;

  // Hooks - use admin hooks if in admin mode
  const regularStatus = useTemplateBuildStatus(isAdminMode ? null : effectiveTaskId);
  const adminStatus = useAdminTemplateBuildStatus(isAdminMode ? effectiveTaskId : null);
  const { isReadyForChat } = isAdminMode ? adminStatus : regularStatus;
  // targetUserId is only available from admin status hook
  const statusTargetUserId = isAdminMode ? adminStatus.targetUserId : null;

  const chatActions = useTemplateBuildActions(effectiveTaskId, isFeatureReady);

  // Save mutations - use admin mutation if in admin mode
  const [saveTemplateBuild, { isLoading: isSavingRegular }] = useSaveTemplateBuildMutation();
  const [adminSaveTemplateBuild, { isLoading: isSavingAdmin }] = useAdminBuildSaveMutation();
  const isSaving = isAdminMode ? isSavingAdmin : isSavingRegular;

  // Handle initialization
  useEffect(() => {
    if (!isFeatureReady) return;

    if (urlTaskId) {
      // Initialize with URL task ID
      dispatch(initializeTemplateBuild({ taskId: urlTaskId, threadId: null }));
      dispatch(setTaskId(urlTaskId));
    } else {
      // No taskId in URL - always reset to upload screen state
      // This handles both fresh starts and navigation back from a build
      dispatch(initializeTemplateBuild({ taskId: null, threadId: null }));
    }
  }, [isFeatureReady, urlTaskId, dispatch]);

  // Cleanup on unmount - reset state and stop polling
  useEffect(() => {
    return () => {
      dispatch(resetBuild());
    };
  }, [dispatch]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (isAdminMode && targetUserId) {
      // Navigate back to admin provider page (we don't have template ID here, so go to provider)
      navigate(`/admin-v2/provider/${encodeURIComponent(targetUserId)}`);
    } else if (effectiveTaskId && buildStatus !== 'idle') {
      // User has an active build session - go back to upload screen
      dispatch(resetBuild());
      navigate('/templates/build');
    } else {
      navigate('/templates');
    }
  }, [navigate, isAdminMode, targetUserId, effectiveTaskId, buildStatus, dispatch]);

  // Handle save template
  const handleSave = useCallback(async () => {
    if (!effectiveTaskId) return;

    try {
      let result;
      if (isAdminMode) {
        // Use admin save mutation
        result = await adminSaveTemplateBuild({ taskId: effectiveTaskId }).unwrap();
      } else {
        // Use regular save mutation
        result = await saveTemplateBuild({ taskId: effectiveTaskId }).unwrap();
      }

      // Track save event
      templateBuild.completed(effectiveTaskId, {
        template_id: result.template_id,
        section_count: draftTemplate?.sections.length,
      });

      // Update Redux state
      dispatch(setBuildStatus('completed'));
      dispatch(setCreatedTemplateId(result.template_id));

      // Invalidate templates cache so the templates page shows the new/updated template
      dispatch(
        templatesApi.util.invalidateTags([
          { type: 'TemplateList', id: 'LIST' },
          { type: 'Template', id: result.template_id },
        ]),
      );

      const targetUserDisplay =
        isAdminMode && (statusTargetUserId || targetUserId)
          ? ` for user ${statusTargetUserId || targetUserId}`
          : '';

      enqueueSnackbar(`Template saved successfully${targetUserDisplay}!`, { variant: 'success' });

      // Navigate based on mode
      if (isAdminMode && targetUserId) {
        // Navigate back to admin template detail page
        navigate(
          `/admin-v2/provider/${encodeURIComponent(targetUserId)}/templates/${result.template_id}`,
        );
      } else {
        // Navigate to the new template
        navigate(`/templates?id=${result.template_id}`);
      }
    } catch (error: any) {
      console.error('Failed to save template:', error);
      enqueueSnackbar(error?.data?.message || 'Failed to save template. Please try again.', {
        variant: 'error',
      });
    }
  }, [
    effectiveTaskId,
    isAdminMode,
    targetUserId,
    statusTargetUserId,
    saveTemplateBuild,
    adminSaveTemplateBuild,
    templateBuild,
    draftTemplate,
    dispatch,
    enqueueSnackbar,
    navigate,
  ]);

  // Handle send message
  const handleSendMessage = useCallback(
    (message: string) => {
      chatActions.sendMessage(message);
    },
    [chatActions],
  );

  // Handle select options
  const handleSelectOptions = useCallback(
    (selectedIds: string[]) => {
      chatActions.selectOptions(selectedIds);
    },
    [chatActions],
  );

  // Handle confirm
  const handleConfirm = useCallback(
    (confirmed: boolean) => {
      chatActions.submitConfirmation(confirmed);
    },
    [chatActions],
  );

  // Show loading if feature not ready
  if (!isFeatureReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  // Chat is always visible when ready (not dismissable in template builder)
  const showChat = isReadyForChat;

  // Main content JSX
  const mainContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Admin Mode Banner */}
        {isAdminMode && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            <AlertTitle>Admin Mode</AlertTitle>
            <Typography variant="body2">
              Editing template on behalf of user:{' '}
              <strong>{statusTargetUserId || targetUserId || 'Unknown'}</strong>
            </Typography>
          </Alert>
        )}

        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isAdminMode ? 'Edit Template (Admin)' : 'Create Template'}
            </Typography>
          </Box>

          {/* Save Button - only show when ready for review */}
          {buildStatus === 'ready_for_review' && draftTemplate && (
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving || !draftTemplate.sections.length}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {isSaving ? 'Saving...' : 'Save Template'}
            </Button>
          )}
        </Box>

        {/* Work Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          <TemplateWorkArea />
        </Box>
      </Box>

      {/* Chat Sidebar */}
      {showChat && (
        <Box
          sx={{
            width: CHAT_WIDTH,
            flexShrink: 0,
            height: '100%',
            bgcolor: theme.palette.background.paper,
            borderLeft: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TemplateBuilderChat
            taskId={effectiveTaskId}
            messages={messages}
            assistantStatus={assistantStatus}
            onSendMessage={handleSendMessage}
            onSelectOptions={handleSelectOptions}
            onConfirm={handleConfirm}
          />
        </Box>
      )}
    </Box>
  );

  // For admin mode, render without gate
  if (isAdminMode) {
    return mainContent;
  }

  // For regular users, wrap with EntitlementGate
  return (
    <EntitlementGate
      capabilityName="template_creation"
      source="template_build_page"
      secondaryAction={{
        label: 'Go Back',
        onClick: handleBack,
      }}
    >
      {mainContent}
    </EntitlementGate>
  );
};

export default TemplateBuildPage;
